import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ordersAPI, reviewsAPI, referralsAPI } from '../services/api';
import { Package, ShoppingBag, Unlock, Star, Gift } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ReviewModal from '../components/ReviewModal';
import ReferralSection from '../components/ReferralSection';

const Dashboard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [ordersRes, pendingRes, refStats] = await Promise.all([
        ordersAPI.getAll(),
        reviewsAPI.getPending(),
        referralsAPI.getStats()
      ]);
      
      setOrders(ordersRes.data);
      setPendingReviews(pendingRes.data);
      setReferralStats(refStats.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleReviewSuccess = () => {
    fetchData();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    navigate('/');
    return null;
  }

  const totalSpent = orders.reduce((sum, order) => sum + order.total, 0);
  const totalSavings = Math.round(totalSpent * 0.72);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-4xl font-bold mb-2">Welcome back, {user.name}!</h1>
          <p className="text-sm md:text-base text-gray-600">Manage your orders, reviews, and referrals</p>
        </div>

        {/* Unlock Alert */}
        {user.purchase_unlocks === 0 && (
          <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4 md:p-6 mb-6 md:mb-8">
            <div className="flex items-start gap-3 md:gap-4">
              <Unlock className="h-6 w-6 md:h-8 md:w-8 text-red-600 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="text-lg md:text-xl font-bold text-red-900 mb-2">No Purchase Unlocks Available!</h3>
                <p className="text-sm md:text-base text-red-800 mb-4">
                  You need to submit reviews or refer friends to unlock your next purchase.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  {pendingReviews.length > 0 && (
                    <Button onClick={() => setShowReviewModal(true)} className="bg-red-600 hover:bg-red-700 w-full sm:w-auto text-sm md:text-base">
                      Submit Review ({pendingReviews.length} pending)
                    </Button>
                  )}
                  <Button variant="outline" className="w-full sm:w-auto text-sm md:text-base" onClick={() => document.getElementById('referrals').scrollIntoView({ behavior: 'smooth' })}>
                    Refer Friends
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-6 mb-6 md:mb-8">
          <Card className="p-4 md:p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-xs md:text-sm mb-1">Purchase Unlocks</p>
                <p className="text-2xl md:text-4xl font-bold text-green-700">{user.purchase_unlocks || 0}</p>
              </div>
              <Unlock className="h-8 w-8 md:h-12 md:w-12 text-green-600" />
            </div>
            <p className="text-xs text-gray-600 mt-2">Available purchases</p>
          </Card>
          <Card className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-xs md:text-sm">Total Orders</p>
                <p className="text-2xl md:text-3xl font-bold">{orders.length}</p>
              </div>
              <Package className="h-8 w-8 md:h-12 md:w-12 text-red-600" />
            </div>
          </Card>
          <Card className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-xs md:text-sm">Total Spent</p>
                <p className="text-lg md:text-2xl font-bold">₹{totalSpent.toLocaleString()}</p>
              </div>
              <ShoppingBag className="h-8 w-8 md:h-12 md:w-12 text-red-600" />
            </div>
          </Card>
          <Card className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-xs md:text-sm">Total Savings</p>
                <p className="text-lg md:text-2xl font-bold text-green-600">₹{totalSavings.toLocaleString()}</p>
              </div>
              <Gift className="h-8 w-8 md:h-12 md:w-12 text-green-600" />
            </div>
          </Card>
          <Card className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-xs md:text-sm">Pending Reviews</p>
                <p className="text-2xl md:text-3xl font-bold text-orange-600">{pendingReviews.length}</p>
              </div>
              <Star className="h-8 w-8 md:h-12 md:w-12 text-orange-600" />
            </div>
          </Card>
        </div>

        {/* Pending Reviews */}
        {pendingReviews.length > 0 && (
          <Card className="p-4 md:p-6 mb-6 md:mb-8 border-2 border-orange-200 bg-orange-50">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-orange-900">Pending Reviews</h2>
                <p className="text-xs md:text-sm text-orange-700">Submit reviews to unlock more purchases</p>
              </div>
              <Button onClick={() => { setSelectedOrder(pendingReviews[0]); setShowReviewModal(true); }} className="bg-orange-600 hover:bg-orange-700 w-full sm:w-auto text-sm md:text-base">
                Submit Review
              </Button>
            </div>
            <div className="space-y-3">
              {pendingReviews.slice(0, 3).map((order) => (
                <div key={order.id} className="bg-white p-3 md:p-4 rounded-lg border border-orange-200">
                  <div className="flex justify-between items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm md:text-base truncate">Order #{order.id.slice(0, 8)}</p>
                      <p className="text-xs md:text-sm text-gray-600">{new Date(order.created_at).toLocaleDateString()}</p>
                    </div>
                    <Button size="sm" onClick={() => { setSelectedOrder(order); setShowReviewModal(true); }} className="bg-orange-600 hover:bg-orange-700 text-xs md:text-sm flex-shrink-0">
                      Review Now
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Referral Section */}
        {referralStats && (
          <div id="referrals" className="mb-6 md:mb-8">
            <ReferralSection
              referralCode={referralStats.referral_code}
              totalReferrals={referralStats.total_referrals}
              totalUnlocksEarned={referralStats.total_unlocks_earned}
            />
          </div>
        )}

        {/* Recent Orders */}
        <Card className="p-4 md:p-6 mb-6 md:mb-8">
          <div className="flex justify-between items-center mb-4 md:mb-6">
            <h2 className="text-xl md:text-2xl font-bold">Recent Orders</h2>
          </div>
          {ordersLoading ? (
            <p className="text-gray-600 text-center py-8">Loading orders...</p>
          ) : orders.length === 0 ? (
            <p className="text-gray-600 text-center py-8">No orders yet</p>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => (
                <div key={order.id} className="border rounded-lg p-3 md:p-4">
                  <div className="flex justify-between items-start mb-3 gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm md:text-base truncate">Order #{order.id.slice(0, 8)}</p>
                      <p className="text-xs md:text-sm text-gray-600">{new Date(order.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-bold text-red-600 text-sm md:text-base">₹{order.total.toLocaleString()}</p>
                      <span className={`text-xs md:text-sm px-2 py-1 rounded-full ${
                        order.status === 'Delivered' ? 'bg-green-100 text-green-700' : 
                        order.status === 'Processing' ? 'bg-blue-100 text-blue-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {order.status}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {order.items.map((item, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <img
                          src={item.product_image}
                          alt={item.product_name}
                          className="w-12 h-12 object-cover rounded"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-semibold">{item.product_name}</p>
                          <p className="text-xs text-gray-600">Qty: {item.quantity}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Account Info */}
        <Card className="p-6">
          <h2 className="text-2xl font-bold mb-6">Account Information</h2>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-600">Name</label>
              <p className="font-semibold">{user.name}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Email</label>
              <p className="font-semibold">{user.email}</p>
            </div>
          </div>
        </Card>
      </div>

      <Footer />

      {/* Review Modal */}
      {showReviewModal && selectedOrder && (
        <ReviewModal
          order={selectedOrder}
          onClose={() => {
            setShowReviewModal(false);
            setSelectedOrder(null);
          }}
          onSuccess={handleReviewSuccess}
        />
      )}
    </div>
  );
};

export default Dashboard;
